import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "@/shared/components/loading-spinner";
import { ButtonSpinner } from "@/shared/components/button-spinner";
import { QuizBackButton } from "./quiz-back-button";
import { QuizStepHeader } from "./quiz-step-header";
import { SignatureSheet } from "./signature-sheet";
import { PdfPagePreview } from "./pdf-page-preview";
import { turbopayApi } from "../api";
import { useClaimsStore } from "../store";
import { sha256Hex, bytesToBase64, pdfBlob } from "../domain/hash";
import { fillAuthorizationPdf } from "../domain/pdf";
import type { FilingPackageItem, SignatureMethod } from "../types";

interface SigningStepProps {
  onBack: () => void;
  onAdvance: () => void;
}

export function SigningStep({ onBack, onAdvance }: SigningStepProps) {
  const selected = useClaimsStore((s) => s.selected);
  const profile = useClaimsStore((s) => s.profile);
  const packages = useClaimsStore((s) => s.packages);
  const setPackages = useClaimsStore((s) => s.setPackages);
  const signed = useClaimsStore((s) => s.signedAuthorizations);
  const upsertSigned = useClaimsStore((s) => s.upsertSigned);

  const [loading, setLoading] = useState(packages.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [preview, setPreview] = useState<{ id: string; url: string } | null>(
    null,
  );
  const [signing, setSigning] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const templatesRef = useRef(new Map<string, ArrayBuffer>());

  const supported = useMemo(
    () =>
      packages.filter(
        (item) =>
          item.supported && item.document && item.template && item.consent,
      ),
    [packages],
  );

  const current = supported[index];
  const isLast = index >= supported.length - 1;
  const isSigned = current
    ? signed.some((a) => a.settlement_id === current.settlement_id)
    : false;

  const selectedIdsKey = selected
    .map((s) => s.id)
    .slice()
    .sort()
    .join(",");
  const packageIdsKey = packages
    .map((p) => p.settlement_id)
    .slice()
    .sort()
    .join(",");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = selected.map((s) => s.id);
      if (ids.length === 0) {
        setLoading(false);
        return;
      }
      if (packageIdsKey === selectedIdsKey && packages.length > 0) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await turbopayApi.createPackages(ids);
        if (cancelled) return;
        setIndex(0);
        setPackages(res.items ?? []);
      } catch (err) {
        if (cancelled) return;
        console.error("packages failed", err);
        setError("Could not prepare claim documents. Try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packageIdsKey, selected, selectedIdsKey, setPackages, packages.length]);

  const loadTemplate = useCallback(
    async (item: FilingPackageItem): Promise<ArrayBuffer> => {
      const doc = item.document;
      if (!doc) throw new Error("Missing document");
      const cached = templatesRef.current.get(doc.sha256);
      if (cached) return cached;
      const bytes = await turbopayApi.downloadDocument(doc.url);
      const hex = await sha256Hex(bytes);
      if (hex !== doc.sha256.toLowerCase()) {
        throw new Error("document_hash_mismatch");
      }
      templatesRef.current.set(doc.sha256, bytes);
      return bytes;
    },
    [],
  );

  const renderPreview = useCallback(
    async (item: FilingPackageItem, signaturePng?: Uint8Array) => {
      const existing = signed.find(
        (a) => a.settlement_id === item.settlement_id,
      );
      if (existing && !signaturePng) {
        const binary = atob(existing.document_base64);
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        return URL.createObjectURL(pdfBlob(bytes));
      }
      const template = await loadTemplate(item);
      const filled = await fillAuthorizationPdf({
        templateBytes: template,
        item,
        profile,
        signaturePng,
      });
      return URL.createObjectURL(pdfBlob(filled));
    },
    [loadTemplate, profile, signed],
  );

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    let url: string | null = null;
    renderPreview(current)
      .then((next) => {
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        url = next;
        setPreview({ id: current.settlement_id, url: next });
      })
      .catch((err) => {
        console.error("preview failed", err);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [current, renderPreview]);

  const handleSigned = async (png: Uint8Array, method: SignatureMethod) => {
    if (!current?.document || !current.template || !current.consent) return;
    setSignOpen(false);
    setSigning(true);
    try {
      const template = await loadTemplate(current);
      const filled = await fillAuthorizationPdf({
        templateBytes: template,
        item: current,
        profile,
        signaturePng: png,
      });
      const hash = await sha256Hex(filled);
      upsertSigned({
        settlement_id: current.settlement_id,
        template_id: current.template.id,
        template_version: current.template.version,
        template_sha256: current.template.sha256,
        consent_version: current.consent.version,
        signature_method: method,
        signed_at_client: new Date().toISOString(),
        document_sha256: hash,
        document_base64: bytesToBase64(filled),
      });
      const url = URL.createObjectURL(pdfBlob(filled));
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return { id: current.settlement_id, url };
      });
    } catch (err) {
      console.error("sign fill failed", err);
      setError("Could not apply your signature. Try again.");
    } finally {
      setSigning(false);
    }
  };

  const goNext = () => {
    if (isLast || supported.length === 0) {
      onAdvance();
      return;
    }
    setIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (index > 0) {
      setIndex((i) => i - 1);
      return;
    }
    onBack();
  };

  const fullName = [profile.first_name, profile.middle_name, profile.last_name]
    .filter((p) => p.trim())
    .join(" ");

  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-mf-neutral-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error && supported.length === 0) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-mf-neutral-50 px-4 py-3 font-body">
        <QuizBackButton onClick={onBack} />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <p className="text-base text-mf-error">{error}</p>
          <button
            type="button"
            onClick={onAdvance}
            className="flex h-12 w-full max-w-xs items-center justify-center rounded-full bg-mf-neutral-900 text-[15px] font-bold text-white"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (supported.length === 0) {
    return (
      <div className="flex min-h-[100dvh] flex-col bg-mf-neutral-50 px-4 py-3 font-body">
        <QuizBackButton onClick={onBack} />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <p className="text-lg font-bold text-mf-neutral-900">
            No documents to sign
          </p>
          <p className="text-sm text-mf-neutral-600">
            These claims don&apos;t need a signature right now. You can continue
            to checkout.
          </p>
          <button
            type="button"
            onClick={onAdvance}
            className="flex h-12 w-full max-w-xs items-center justify-center rounded-full bg-mf-neutral-900 text-[15px] font-bold text-white"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  const progress = (index + 1) / supported.length;
  const company =
    selected.find((s) => s.id === current.settlement_id)?.company ||
    current.name;
  const previewUrl = preview?.id === current.settlement_id ? preview.url : null;
  const filling = signing || !previewUrl;

  return (
    <div className="flex h-[100dvh] flex-col bg-mf-neutral-50 font-body text-mf-neutral-900">
      <div className="relative min-h-0 flex-1">
        <div className="h-full overflow-y-auto">
          <QuizStepHeader
            onBack={goPrev}
            trailing={
              <div className="rounded-full border border-mf-neutral-200 px-4 py-2">
                <span className="text-sm font-medium leading-4">
                  {index + 1} / {supported.length}
                </span>
              </div>
            }
          >
            <div className="h-1 w-full overflow-hidden rounded-sm bg-mf-neutral-100">
              <div
                className="h-full rounded-sm bg-mf-green transition-all"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
          </QuizStepHeader>
          <div className="flex flex-col items-center gap-5 px-4 pb-4 pt-2">
            <div className="flex flex-col gap-2 text-center">
              <h1 className="font-display text-xl font-bold leading-6">
                {company} claim
              </h1>
              <p className="text-sm leading-4 text-mf-neutral-400">
                We&apos;ve pre-filled the form using your details. Review it
                below
              </p>
            </div>

            <div className="relative h-[474px] w-full overflow-hidden rounded-[20px] bg-white shadow-[0_0_16px_rgba(26,22,19,0.03)]">
              {filling && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                  <LoadingSpinner />
                </div>
              )}
              {previewUrl && (
                <PdfPagePreview
                  url={previewUrl}
                  title={`${company} authorization`}
                />
              )}
              {!isSigned && (
                <button
                  type="button"
                  onClick={() => setSignOpen(true)}
                  className="absolute bottom-16 left-24 z-20 flex h-9 items-center justify-center rounded-full border-2 border-white bg-mf-error px-4 text-xs font-bold text-mf-neutral-50 shadow-[0_0_8px_rgba(26,22,19,0.03)]"
                >
                  CLICK TO SIGN
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-b from-transparent to-mf-neutral-50" />
      </div>

      <div className="flex shrink-0 gap-2 bg-white px-4 py-3">
        <button
          type="button"
          onClick={goNext}
          className="flex h-12 flex-1 items-center justify-center rounded-full border-2 border-mf-neutral-200 text-[15px] font-bold text-mf-neutral-900"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={!isSigned}
          className={cn(
            "flex h-12 flex-1 items-center justify-center rounded-full text-[15px] font-bold text-white",
            isSigned
              ? "bg-mf-neutral-900"
              : "cursor-not-allowed bg-mf-neutral-900/20",
          )}
        >
          {filling ? <ButtonSpinner /> : isLast ? "Submit" : "Next PDF"}
        </button>
      </div>

      {signOpen && current && (
        <SignatureSheet
          open={signOpen}
          onClose={() => setSignOpen(false)}
          consentText={current.consent?.text ?? null}
          defaultName={fullName}
          onSigned={(png, method) => void handleSigned(png, method)}
        />
      )}
    </div>
  );
}
